import React, { useContext, createContext, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

// Create a context for the confirmation modal
const ConfirmModalContext = createContext();

/**
 * Provider component for the confirmation modal
 */
export const ConfirmModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'warning'
  });

  const openModal = (options) => {
    setModalState({ ...modalState, isOpen: true, ...options });
  };

  const closeModal = () => {
    setModalState({ ...modalState, isOpen: false });
  };

  const handleConfirm = () => {
    modalState.onConfirm();
    closeModal();
  };

  return (
    <ConfirmModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {/* The actual modal component */}
      <div className={`modal ${modalState.isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <div className="flex items-start gap-4">
            <div className={`text-${modalState.type} mt-1`}>
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-bold text-${modalState.type}`}>{modalState.title}</h3>
              <p className="py-4">{modalState.message}</p>
              <div className="modal-action">
                <button 
                  className={`btn btn-${modalState.type}`} 
                  onClick={handleConfirm}
                >
                  {modalState.confirmText}
                </button>
                <button 
                  className="btn btn-outline" 
                  onClick={closeModal}
                >
                  {modalState.cancelText}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop" onClick={closeModal}></div>
      </div>
    </ConfirmModalContext.Provider>
  );
};

/**
 * Custom hook to use the confirmation modal
 * @returns {Object} The confirmation modal functions
 * 
 * Example usage:
 * const { confirm } = useConfirmModal();
 * 
 * // Later in a component:
 * const handleDelete = () => {
 *   confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure you want to delete this item?',
 *     onConfirm: () => { 
 *       // Actual delete logic here
 *       deleteItem(id);
 *     }
 *   });
 * };
 */
export const useConfirmModal = () => {
  const context = useContext(ConfirmModalContext);
  
  if (!context) {
    throw new Error('useConfirmModal must be used within a ConfirmModalProvider');
  }
  
  const confirm = (options) => {
    context.openModal({
      title: options.title || 'Confirm Action',
      message: options.message || 'Are you sure you want to proceed?',
      onConfirm: options.onConfirm || (() => {}),
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      type: options.type || 'warning'
    });
  };
  
  return { confirm };
};

export default ConfirmModalProvider; 